import { useState, useRef, useCallback, cloneElement, isValidElement } from 'react'
import { createPortal } from 'react-dom'
import '../styles/components/tooltip.css'

/**
 * Tooltip that renders in a portal so it always appears above other content.
 * Wraps a single trigger element; shows tooltip on hover.
 */
export default function Tooltip({ text, children }) {
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef(null)

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const gap = 6
    setPosition({
      top: rect.top - gap,
      left: rect.left + rect.width / 2,
    })
  }, [])

  const handleMouseEnter = () => {
    updatePosition()
    setVisible(true)
  }

  const handleMouseLeave = () => {
    setVisible(false)
  }

  const tooltipEl = visible && text ? (
    <div
      className="tooltip-portal"
      role="tooltip"
      style={{
        position: 'fixed',
        left: position.left,
        top: position.top,
        transform: 'translate(-50%, -100%)',
      }}
    >
      {text}
    </div>
  ) : null

  const trigger =
    isValidElement(children) &&
    cloneElement(children, {
      ref: (el) => {
        triggerRef.current = el
        const chRef = children.ref
        if (typeof chRef === 'function') chRef(el)
        else if (chRef) chRef.current = el
      },
      onMouseEnter: (e) => {
        children.props.onMouseEnter?.(e)
        handleMouseEnter()
      },
      onMouseLeave: (e) => {
        children.props.onMouseLeave?.(e)
        handleMouseLeave()
      },
    })

  return (
    <>
      {trigger}
      {typeof document !== 'undefined' && document.body
        ? createPortal(tooltipEl, document.body)
        : null}
    </>
  )
}
